import { useState, useCallback, useRef } from "react";

const SECTION_TYPES = [
  {
    type: "storyBlock",
    label: "Story Block",
    icon: "✦",
    hint: "Heading + paragraphs & bullet lists mixed in any order",
    color: "#1a56db",
    defaultData: {
      heading: "Your Section Heading",
      blocks: [{ kind: "paragraph", text: "" }],
    },
  },
  {
    type: "threeGrid",
    label: "Three Image Grid",
    icon: "⊞",
    hint: "Three side-by-side images",
    color: "#0e9f6e",
    defaultData: { images: ["", "", ""] },
  },
  {
    type: "testimonial",
    label: "Testimonial",
    icon: "❝",
    hint: "Pull-quote with author name, role, and image",
    color: "#7e3af2",
    defaultData: { image: "", quote: "", name: "", role: "" },
  },
  {
    type: "statsSection",
    label: "Stats / Count-Up",
    icon: "▲",
    hint: "Left heading + any number of stats on the right",
    color: "#ff5a1f",
    defaultData: {
      heading: "Real impact achieved through connected systems",
      stats: [
        { number: "", label: "" },
        { number: "", label: "" },
        { number: "", label: "" },
      ],
    },
  },
  {
    type: "fullWidthBanner",
    label: "Full-Width Banner Image",
    icon: "◻",
    hint: "Edge-to-edge background image strip",
    color: "#c81e1e",
    defaultData: { imageUrl: "", aspectRatio: "20/9" },
  },
];

function generateHTML(sections) {
  return sections
    .map((s) => {
      const d = s.data;
      switch (s.type) {
        case "storyBlock": {
          const lines = d.blocks.flatMap((b) => {
            if (b.kind === "paragraph") return [`    <p>${b.text}</p>`];
            if (b.kind === "bullets")
              return [
                `    <ul>`,
                ...b.items.map((li) => `      <li>${li}</li>`),
                `    </ul>`,
              ];
            return [];
          });
          return [
            `<div class="container">`,
            `  <div class="storyBlock" style="padding:6rem 0; color:#292929;">`,
            `    <h2>${d.heading}</h2>`,
            ...lines,
            `  </div>`,
            `</div>`,
          ].join("\n");
        }

        case "threeGrid":
          return [
            `<div class="container">`,
            `  <div class="threeGrid">`,
            ...d.images.map((src) => `    <img src="${src}" alt="" class="gridImg">`),
            `  </div>`,
            `</div>`,
          ].join("\n");

        case "testimonial":
          return [
            `<div class="storytestimonialContainer" style="color:#292929;">`,
            `  <div class="storytestimonialLeftDiv">`,
            `    <img src="${d.image}" alt="${d.name}">`,
            `  </div>`,
            `  <div class="storytestimonialRightDiv">`,
            `    <h1>"${d.quote}"</h1>`,
            `    <p class="storytestimonialName">${d.name}</p>`,
            `    <p class="storytestimonialRole">${d.role}</p>`,
            `  </div>`,
            `</div>`,
          ].join("\n");

        case "statsSection":
          return [
            `<div class="container" style="color:#292929; padding-bottom:6rem;">`,
            `  <section class="blogCountUpSection storyBlock">`,
            `    <div class="blogCountUpSection__wrapper">`,
            `      <div class="blogCountUpSection__left">`,
            `        <h1>${d.heading}</h1>`,
            `      </div>`,
            `      <div class="blogCountUpSection__right">`,
            ...d.stats.map((st) =>
              [
                `        <div class="blogCountUpSection__stat">`,
                `          <span class="blogCountUpSection__number">${st.number}</span>`,
                `          <p>${st.label}</p>`,
                `        </div>`,
              ].join("\n")
            ),
            `      </div>`,
            `    </div>`,
            `  </section>`,
            `</div>`,
          ].join("\n");

        case "fullWidthBanner":
          return [
            `<div class="storyBlock" style="background-image:url('${d.imageUrl}');background-size:cover;background-position:top;background-repeat:no-repeat;width:100%;aspect-ratio:${d.aspectRatio};margin-bottom:6rem;">`,
            `</div>`,
          ].join("\n");

        default:
          return "";
      }
    })
    .join("\n\n");
}

/* ═══════════════════════════════════════════════════════════════════════
   ANCHOR LINK POPUP
═══════════════════════════════════════════════════════════════════════ */
function AnchorPopup({ anchorState, onInsert, onClose }) {
  const [href, setHref] = useState("");
  const [title, setTitle] = useState("");
  const [label, setLabel] = useState(anchorState.selectedText || "");
  const isValid = href.trim() && label.trim();

  const doInsert = () => {
    if (!isValid) return;
    onInsert(`<a href="${href.trim()}" title="${title.trim()}">${label.trim()}</a>`);
    onClose();
  };

  const iStyle = {
    width: "100%", boxSizing: "border-box", padding: "8px 12px",
    border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13.5,
    fontFamily: "inherit", color: "#111", outline: "none",
  };
  const iFocus = {
    onFocus: (e) => (e.target.style.borderColor = "#1a56db"),
    onBlur: (e) => (e.target.style.borderColor = "#e5e7eb"),
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, width: 440, maxWidth: "95vw", boxShadow: "0 24px 64px rgba(0,0,0,.2)", overflow: "hidden" }}>
        <div style={{ background: "linear-gradient(135deg,#1a56db,#1e40af)", padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>🔗 Insert Anchor Link</span>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,.2)", border: "none", color: "#fff", borderRadius: 6, width: 28, height: 28, cursor: "pointer", fontSize: 16 }}>×</button>
        </div>
        <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
          {[
            ["Link Label *", label, setLabel, "Visible link text", true],
            ["href (URL) *", href, setHref, "/services/your-page", false],
            ["title (SEO tooltip)", title, setTitle, "Descriptive tooltip", false],
          ].map(([lbl, val, setter, ph, af]) => (
            <div key={lbl}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#374151", marginBottom: 4 }}>{lbl}</div>
              <input autoFocus={af} value={val} onChange={(e) => setter(e.target.value)} placeholder={ph} style={iStyle} {...iFocus} />
            </div>
          ))}
          {label && href && (
            <div style={{ background: "#f0f7ff", border: "1.5px solid #bfdbfe", borderRadius: 10, padding: "10px 14px" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#1a56db", textTransform: "uppercase", marginBottom: 5 }}>Preview</div>
              <code style={{ fontSize: 12, color: "#1e40af", wordBreak: "break-all" }}>
                {`<a href="${href}"${title ? ` title="${title}"` : ""}>${label}</a>`}
              </code>
            </div>
          )}
        </div>
        <div style={{ borderTop: "1px solid #f0f0f0", padding: "12px 20px", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "7px 16px", borderRadius: 8, border: "1.5px solid #e5e7eb", background: "#f9fafb", color: "#374151", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          <button onClick={doInsert} disabled={!isValid}
            style={{ padding: "7px 20px", borderRadius: 8, border: "none", background: isValid ? "linear-gradient(135deg,#1a56db,#1e40af)" : "#e5e7eb", color: isValid ? "#fff" : "#9ca3af", fontWeight: 700, fontSize: 13, cursor: isValid ? "pointer" : "default", fontFamily: "inherit" }}>
            Insert Link
          </button>
        </div>
      </div>
    </div>
  );
}

function RichTextField({ value, onChange, placeholder, rows = 3 }) {
  const taRef = useRef(null);
  const [anchorState, setAnchorState] = useState(null);

  const openAnchor = () => {
    const ta = taRef.current;
    if (!ta) return;
    setAnchorState({
      selStart: ta.selectionStart,
      selEnd: ta.selectionEnd,
      selectedText: value.slice(ta.selectionStart, ta.selectionEnd),
    });
  };

  const insertAnchor = (tag) => {
    if (!anchorState) return;
    const { selStart, selEnd } = anchorState;
    onChange(value.slice(0, selStart) + tag + value.slice(selEnd));
    setTimeout(() => {
      if (taRef.current) {
        taRef.current.focus();
        const p = selStart + tag.length;
        taRef.current.setSelectionRange(p, p);
      }
    }, 50);
  };

  return (
    <div>
      <div style={{ position: "relative" }}>
        <textarea
          ref={taRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          style={{
            width: "100%", boxSizing: "border-box", padding: "9px 44px 9px 12px",
            border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13.5,
            fontFamily: "inherit", color: "#111", background: "#fff",
            outline: "none", resize: "vertical", lineHeight: 1.6,
          }}
          onFocus={(e) => (e.target.style.borderColor = "#1a56db")}
          onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
        />
        <button
          type="button"
          onClick={openAnchor}
          title="Insert anchor link"
          style={{
            position: "absolute", top: 7, right: 7, width: 28, height: 28,
            borderRadius: 6, border: "1.5px solid #bfdbfe", background: "#eff6ff",
            color: "#1a56db", fontSize: 14, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#1a56db"; e.currentTarget.style.color = "#fff"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#eff6ff"; e.currentTarget.style.color = "#1a56db"; }}
        >🔗</button>
      </div>
      <div style={{ fontSize: 10.5, color: "#b0b8c8", marginTop: 3 }}>Select text → 🔗 to wrap in a link</div>
      {anchorState && (
        <AnchorPopup anchorState={anchorState} onInsert={insertAnchor} onClose={() => setAnchorState(null)} />
      )}
    </div>
  );
}

const Label = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6b7280", marginBottom: 5 }}>
    {children}
  </div>
);

const Req = () => <span style={{ color: "#dc2626", marginLeft: 2 }}>*</span>;

const Input = ({ value, onChange, placeholder, style = {} }) => (
  <input
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    style={{
      width: "100%", boxSizing: "border-box", padding: "9px 12px",
      border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13.5,
      fontFamily: "inherit", color: "#111", background: "#fff",
      outline: "none", transition: "border-color .15s", ...style,
    }}
    onFocus={(e) => (e.target.style.borderColor = "#1a56db")}
    onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
  />
);

const Textarea = ({ value, onChange, placeholder, rows = 3 }) => (
  <textarea
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    style={{
      width: "100%", boxSizing: "border-box", padding: "9px 12px",
      border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13.5,
      fontFamily: "inherit", color: "#111", background: "#fff",
      outline: "none", resize: "vertical",
    }}
    onFocus={(e) => (e.target.style.borderColor = "#1a56db")}
    onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
  />
);

const SmallBtn = ({ onClick, children, danger, accent, muted }) => (
  <button
    onClick={onClick}
    style={{
      padding: "5px 11px", borderRadius: 6,
      border: `1.5px solid ${danger ? "#fca5a5" : accent ? "#93c5fd" : "#e5e7eb"}`,
      background: danger ? "#fff5f5" : accent ? "#eff6ff" : "#f9fafb",
      color: danger ? "#dc2626" : accent ? "#1a56db" : "#374151",
      fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
    }}
  >
    {children}
  </button>
);

const XBtn = ({ onClick }) => (
  <button
    onClick={onClick}
    style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "0 4px", flexShrink: 0 }}
  >×</button>
);

const ImgPreview = ({ src }) =>
  src ? (
    <img src={src} alt="" style={{ marginTop: 8, width: "100%", maxHeight: 110, objectFit: "cover", borderRadius: 8, border: "1px solid #e5e7eb" }} />
  ) : null;

function MetaCard({ title, icon, children }) {
  return (
    <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e5e7eb", overflow: "hidden", marginBottom: 16 }}>
      <div style={{ padding: "14px 20px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <span style={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>{title}</span>
      </div>
      <div style={{ padding: "20px 20px" }}>{children}</div>
    </div>
  );
}


function MetadataForm({ meta, onChange }) {

  const set = (k, v) => onChange({ ...meta, [k]: v });

  const addTech = () =>
    onChange({ ...meta, techonolgyStack: [...meta.techonolgyStack, { id: String(Date.now()), name: "" }] });
  const removeTech = (id) =>
    onChange({ ...meta, techonolgyStack: meta.techonolgyStack.filter((t) => t.id !== id) });
  const updateTech = (id, name) =>
    onChange({ ...meta, techonolgyStack: meta.techonolgyStack.map((t) => (t.id === id ? { ...t, name } : t)) });
  const moveTech = (i, dir) => {
    if (i + dir < 0 || i + dir >= meta.techonolgyStack.length) return;
    const next = [...meta.techonolgyStack];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    onChange({ ...meta, techonolgyStack: next });
  };

  const iBase = {
    width: "100%", boxSizing: "border-box", padding: "9px 12px",
    border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13.5,
    fontFamily: "inherit", color: "#111", background: "#fff", outline: "none",
  };
  const iFB = {
    onFocus: (e) => (e.target.style.borderColor = "#1a56db"),
    onBlur: (e) => (e.target.style.borderColor = "#e5e7eb"),
  };

  return (
    <div style={{ maxWidth: 820, margin: "0 auto", padding: "32px 20px 80px" }}>

      <MetaCard title="Basic Information" icon="🏢">
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                 <div>
            <Label>Customer / Company Name <Req /></Label>
            <Input value={meta.customer} onChange={(v) => set("customer", v)} placeholder="Pacific West Diagnostic Imaging" />
          </div>
          <div>
            <Label>Headline <Req /></Label>
            <Input
              value={meta.headline}
              onChange={(v) => { set("headline", v); }}
              placeholder="EDI Processor Bridge for Salesforce and Billing"
            />
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
              Fills both <code>headline</code> and <code>detailPageHeadline</code>
            </div>
          </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <Label>URL Slug <Req /></Label>
              <div style={{ display: "flex" }}>
                <span style={{ padding: "9px 10px", background: "#f1f5f9", border: "1.5px solid #e5e7eb", borderRight: "none", borderRadius: "8px 0 0 8px", fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap" }}>
                  /stories/
                </span>
                <input
                  value={meta.url}
                  onChange={(e) => set("url", e.target.value)}
                  placeholder="edi-processor-bridge"
                  style={{ ...iBase, borderRadius: "0 8px 8px 0", flex: 1 }}
                  {...iFB}
                />
              </div>
            </div>

            <div>
              <Label>Industry <Req /></Label>
             
              <Input
              value={meta.industry}
              onChange={(v) => { set("industry", v);}}
              placeholder="Industry"
            />
            </div>
          </div>

          <div>
            <Label>Order / Position</Label>
            <input
              type="number"
              min={1}
              value={meta.order}
              onChange={(e) => set("order", Number(e.target.value))}
              style={{ ...iBase, width: 100 }}
              {...iFB}
            />
          </div>
        </div>
      </MetaCard>

      <MetaCard title="Images & Branding" icon="🖼️">
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          <div>
            <Label>Card / Background Image URL <Req /></Label>
            <Input
              value={meta.backgroundImage}
              onChange={(v) => { set("backgroundImage", v); }}
              placeholder="https://…"
            />
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
              Used for both <code>backgroundImage</code> and <code>imageUrl</code>
            </div>
            <ImgPreview src={meta.backgroundImage} />
          </div>

          <div>
            <Label>Brand Logo URL</Label>
            <Input value={meta.Brandlogo} onChange={(v) => set("Brandlogo", v)} placeholder="https://…" />
            {meta.Brandlogo && (
              <img
                src={meta.Brandlogo}
                alt="logo"
                style={{ marginTop: 8, maxHeight: 60, objectFit: "contain", border: "1px solid #e5e7eb", borderRadius: 8, padding: 6, background: "#f8fafc" }}
              />
            )}
          </div>
        </div>
      </MetaCard>

      <MetaCard title="Description & Impact" icon="📋">
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <Label>Description <Req /></Label>
            <Textarea
              value={meta.description}
              onChange={(v) => set("description", v)}
              rows={3}
              placeholder="Short description shown on the story card…"
            />
          </div>
          <div>
            <Label>Impact Summary <Req /></Label>
            <Textarea
              value={meta.impact}
              onChange={(v) => set("impact", v)}
              rows={3}
              placeholder="Key outcomes and results from this engagement…"
            />
          </div>
        </div>
      </MetaCard>

      <MetaCard title="Technology Stack" icon="⚙️">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {meta.techonolgyStack.map((t, i) => (
            <div
              key={t.id}
              style={{ display: "flex", gap: 8, alignItems: "center", background: "#f8fafc", border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "10px 12px" }}
            >
              <div style={{ width: 26, height: 26, borderRadius: 6, background: "#1a56db", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
                {i + 1}
              </div>
              <input
                value={t.name}
                onChange={(e) => updateTech(t.id, e.target.value)}
                placeholder="e.g. Salesforce Health Cloud"
                style={{ ...iBase, flex: 1, padding: "7px 10px" }}
                {...iFB}
              />
              <div style={{ display: "flex", gap: 4 }}>
                <SmallBtn onClick={() => moveTech(i, -1)}>↑</SmallBtn>
                <SmallBtn onClick={() => moveTech(i, 1)}>↓</SmallBtn>
                {meta.techonolgyStack.length > 1 && <XBtn onClick={() => removeTech(t.id)} />}
              </div>
            </div>
          ))}
          <div style={{ marginTop: 4 }}>
            <SmallBtn accent onClick={addTech}>+ Add Technology</SmallBtn>
          </div>
        </div>
      </MetaCard>

    </div>
  );
}

function StoryBlockEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });

  const updateBlock = (i, b) => {
    const blocks = data.blocks.map((x, j) => (j === i ? b : x));
    set("blocks", blocks);
  };
  const removeBlock = (i) => set("blocks", data.blocks.filter((_, j) => j !== i));
  const moveBlock = (i, dir) => {
    if (i + dir < 0 || i + dir >= data.blocks.length) return;
    const next = [...data.blocks];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    set("blocks", next);
  };

  const setBulletItem = (bi, ii, v) => {
    const b = data.blocks[bi];
    updateBlock(bi, { ...b, items: b.items.map((x, j) => (j === ii ? v : x)) });
  };
  const addBulletItem = (bi) => {
    const b = data.blocks[bi];
    updateBlock(bi, { ...b, items: [...b.items, ""] });
  };
  const removeBulletItem = (bi, ii) => {
    const b = data.blocks[bi];
    updateBlock(bi, { ...b, items: b.items.filter((_, j) => j !== ii) });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <Label>Heading</Label>
        <Input value={data.heading} onChange={(v) => set("heading", v)} placeholder="Section heading…" />
      </div>

      <div>
        <Label>
          Content Blocks{" "}
          <span style={{ color: "#9ca3af", fontWeight: 400, textTransform: "none", fontSize: 10 }}>
            — paragraphs &amp; bullet lists in any order
          </span>
        </Label>

        {data.blocks.map((block, i) => (
          <div
            key={i}
            style={{
              border: `1.5px solid ${block.kind === "bullets" ? "#dbeafe" : "#e5e7eb"}`,
              borderRadius: 10,
              background: block.kind === "bullets" ? "#f0f7ff" : "#fff",
              padding: "12px 14px",
              marginBottom: 8,
            }}
          >
            
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <span
                style={{
                  fontSize: 10, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase",
                  padding: "2px 8px", borderRadius: 20,
                  background: block.kind === "bullets" ? "#1a56db" : "#6b7280",
                  color: "#fff",
                }}
              >
                {block.kind === "bullets" ? "⁂ Bullet List" : "¶ Paragraph"} · Block {i + 1}
              </span>
              <div style={{ display: "flex", gap: 4 }}>
                <SmallBtn onClick={() => moveBlock(i, -1)}>↑</SmallBtn>
                <SmallBtn onClick={() => moveBlock(i, 1)}>↓</SmallBtn>
                <XBtn onClick={() => removeBlock(i)} />
              </div>
            </div>

            {block.kind === "paragraph" && (
              <RichTextField
                value={block.text}
                onChange={(v) => updateBlock(i, { ...block, text: v })}
                placeholder="Paragraph text… select text then 🔗 to add a link"
                rows={3}
              />
            )}

            {block.kind === "bullets" && (
              <div>
                {block.items.map((item, j) => (
                  <div key={j} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                    <span style={{ color: "#1a56db", fontWeight: 900, fontSize: 20, lineHeight: 1, flexShrink: 0 }}>·</span>
                    <Input value={item} onChange={(v) => setBulletItem(i, j, v)} placeholder={`Bullet ${j + 1}…`} />
                    {block.items.length > 1 && <XBtn onClick={() => removeBulletItem(i, j)} />}
                  </div>
                ))}
                <SmallBtn accent onClick={() => addBulletItem(i)}>+ Add Bullet</SmallBtn>
              </div>
            )}
          </div>
        ))}

        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <SmallBtn accent onClick={() => set("blocks", [...data.blocks, { kind: "paragraph", text: "" }])}>
            + Add Paragraph
          </SmallBtn>
          <SmallBtn onClick={() => set("blocks", [...data.blocks, { kind: "bullets", items: [""] }])}>
            + Add Bullet List
          </SmallBtn>
        </div>
      </div>
    </div>
  );
}

function ThreeGridEditor({ data, onChange }) {
  const setImg = (i, v) => {
    const imgs = [...data.images];
    imgs[i] = v;
    onChange({ ...data, images: imgs });
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {data.images.map((img, i) => (
        <div key={i}>
          <Label>Image {i + 1} URL</Label>
          <Input value={img} onChange={(v) => setImg(i, v)} placeholder="https://…" />
          <ImgPreview src={img} />
        </div>
      ))}
    </div>
  );
}

function TestimonialEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <Label>Author Photo URL</Label>
        <Input value={data.image} onChange={(v) => set("image", v)} placeholder="https://…" />
        {data.image && (
          <img src={data.image} alt="" style={{ marginTop: 8, height: 80, borderRadius: 8, objectFit: "cover", border: "1px solid #e5e7eb" }} />
        )}
      </div>
      <div>
        <Label>Quote <span style={{ color: "#9ca3af", fontWeight: 400, textTransform: "none", fontSize: 10 }}>(surrounding quotes added automatically)</span></Label>
        <Textarea value={data.quote} onChange={(v) => set("quote", v)} placeholder="The testimonial quote…" rows={3} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <Label>Author Name</Label>
          <Input value={data.name} onChange={(v) => set("name", v)} placeholder="Jane Smith" />
        </div>
        <div>
          <Label>Role / Title</Label>
          <Input value={data.role} onChange={(v) => set("role", v)} placeholder="CEO, Company" />
        </div>
      </div>
    </div>
  );
}

function StatsSectionEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });

  const setStat = (i, k, v) => {
    const stats = data.stats.map((s, j) => (j === i ? { ...s, [k]: v } : s));
    set("stats", stats);
  };
  const addStat = () => set("stats", [...data.stats, { number: "", label: "" }]);
  const removeStat = (i) => {
    if (data.stats.length <= 1) return;
    set("stats", data.stats.filter((_, j) => j !== i));
  };
  const moveStat = (i, dir) => {
    if (i + dir < 0 || i + dir >= data.stats.length) return;
    const next = [...data.stats];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    set("stats", next);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <Label>Left Heading</Label>
        <Textarea value={data.heading} onChange={(v) => set("heading", v)} rows={2} placeholder="Big headline on the left side…" />
      </div>

      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <Label>
            Stat Items{" "}
            <span style={{ color: "#9ca3af", fontWeight: 400, textTransform: "none", fontSize: 10 }}>
              — {data.stats.length} item{data.stats.length !== 1 ? "s" : ""}
            </span>
          </Label>
          <SmallBtn accent onClick={addStat}>+ Add Stat</SmallBtn>
        </div>

        {data.stats.map((st, i) => (
          <div
            key={i}
            style={{ background: "#fff7f3", border: "1.5px solid #fed7aa", borderRadius: 10, padding: "12px 14px", marginBottom: 8 }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", padding: "2px 8px", borderRadius: 20, background: "#ff5a1f", color: "#fff" }}>
                Stat {i + 1}
              </span>
              <div style={{ display: "flex", gap: 4 }}>
                <SmallBtn onClick={() => moveStat(i, -1)}>↑</SmallBtn>
                <SmallBtn onClick={() => moveStat(i, 1)}>↓</SmallBtn>
                {data.stats.length > 1 && <XBtn onClick={() => removeStat(i)} />}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 12 }}>
              <div>
                <Label>Number / Value</Label>
                <Input value={st.number} onChange={(v) => setStat(i, "number", v)} placeholder="$2M, 30%, 99…" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={st.label} onChange={(v) => setStat(i, "label", v)} rows={2} placeholder="Short description of this stat…" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FullWidthBannerEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <Label>Image URL</Label>
        <Input value={data.imageUrl} onChange={(v) => set("imageUrl", v)} placeholder="https://…" />
        <ImgPreview src={data.imageUrl} />
      </div>
      <div>
        <Label>Aspect Ratio</Label>
        <select
          value={data.aspectRatio}
          onChange={(e) => set("aspectRatio", e.target.value)}
          style={{
            padding: "9px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8,
            fontSize: 13.5, fontFamily: "inherit", color: "#111", background: "#fff", width: "100%",
          }}
        >
          <option value="20/9">20 : 9 — Wide cinematic (default)</option>
          <option value="16/9">16 : 9 — Standard widescreen</option>
          <option value="3/1">3 : 1 — Slim strip</option>
          <option value="4/1">4 : 1 — Extra slim strip</option>
        </select>
      </div>
    </div>
  );
}

const EDITORS = {
  storyBlock: StoryBlockEditor,
  threeGrid: ThreeGridEditor,
  testimonial: TestimonialEditor,
  statsSection: StatsSectionEditor,
  fullWidthBanner: FullWidthBannerEditor,
};

function SectionPicker({ onSelect, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 20, width: 560, maxWidth: "94vw", maxHeight: "88vh", overflowY: "auto", boxShadow: "0 32px 80px rgba(0,0,0,.22)" }}
      >
        <div style={{ padding: "20px 22px 14px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: "#111" }}>Choose a Section Type</div>
            <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>Any type can be added multiple times</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "#9ca3af" }}>×</button>
        </div>
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }}>
          {SECTION_TYPES.map((t) => (
            <button
              key={t.type}
              onClick={() => { onSelect(t); onClose(); }}
              style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", border: "1.5px solid #e5e7eb", borderRadius: 12, background: "#fafafa", cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "all .15s" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = t.color; e.currentTarget.style.background = "#fff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.background = "#fafafa"; }}
            >
              <div style={{ width: 40, height: 40, borderRadius: 10, background: t.color + "18", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: t.color, fontWeight: 900, flexShrink: 0 }}>
                {t.icon}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#111" }}>{t.label}</div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{t.hint}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function OutputPanel({ meta, sections }) {
  const [view, setView] = useState("json");
  const [copied, setCopied] = useState(false);

  const storyDetail = generateHTML(sections);
  const fullObject = {
    order: meta.order,
    imageUrl: meta.backgroundImage,
    backgroundImage: meta.backgroundImage,
    Brandlogo: meta.Brandlogo,
    customer: meta.customer,
    headline: meta.headline,
    url: meta.url,
    detailPageHeadline: meta.detailPageHeadline,
    industry: meta.industry,
    description: meta.description,
    impact: meta.impact,
    techonolgyStack: meta.techonolgyStack.map((t, i) => ({ id: String(i + 1), name: t.name })),
    storyDetail,
  };

  const jsonText = JSON.stringify(fullObject, null, 2);
  const content = view === "json" ? jsonText : storyDetail;
  const copy = () => { navigator.clipboard?.writeText(content); setCopied(true); setTimeout(() => setCopied(false), 2200); };

  return (
    <div style={{ height: "calc(100vh - 62px)", background: "#0f172a", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 22px", borderBottom: "1px solid #1e293b", flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 4, background: "#1e293b", borderRadius: 8, padding: 3 }}>
          {[["json", "{ }  Full JSON Object"], ["html", "⟨/⟩  storyDetail HTML only"]].map(([id, lbl]) => (
            <button
              key={id}
              onClick={() => setView(id)}
              style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: view === id ? "#334155" : "transparent", color: view === id ? "#e2e8f0" : "#64748b", fontWeight: 600, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit" }}
            >
              {lbl}
            </button>
          ))}
        </div>
        <button
          onClick={copy}
          style={{ padding: "7px 16px", borderRadius: 8, border: "none", background: copied ? "#16a34a" : "#1a56db", color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", transition: "background .2s" }}
        >
          {copied ? "✓ Copied!" : "Copy"}
        </button>
      </div>
      <pre style={{ flex: 1, margin: 0, overflowY: "auto", padding: "20px 24px", fontSize: 12.5, lineHeight: 1.8, color: view === "json" ? "#a3e635" : "#7dd3fc", fontFamily: "'Courier New', Consolas, monospace", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {content || "// Nothing to show yet — fill in Metadata and add Story Sections"}
      </pre>
    </div>
  );
}

function InsertStrip({ onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      style={{ display: "flex", alignItems: "center", margin: "5px 0", opacity: hov ? 1 : 0.3, transition: "opacity .2s" }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      <div style={{ flex: 1, height: 1, background: "#e5e7eb" }} />
      <button
        onClick={onClick}
        style={{ padding: "4px 14px", borderRadius: 20, border: "1.5px dashed #93c5fd", background: "#eff6ff", color: "#1a56db", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}
      >
        + Insert section here
      </button>
      <div style={{ flex: 1, height: 1, background: "#e5e7eb" }} />
    </div>
  );
}

let _uid = 0;
const uid = () => ++_uid;

function StorySectionsBuilder({ sections, setSections }) {
  const [expanded, setExpanded] = useState(null);
  const [picker, setPicker] = useState(null);

  const addSection = useCallback(
    (typeDef) => {
      const s = { id: uid(), type: typeDef.type, data: JSON.parse(JSON.stringify(typeDef.defaultData)) };
      setSections((prev) => {
        if (picker === "end" || picker === null) return [...prev, s];
        const next = [...prev];
        next.splice(picker, 0, s);
        return next;
      });
      setExpanded(s.id);
    },
    [picker, setSections]
  );

  const removeSection = (id) => setSections((p) => p.filter((s) => s.id !== id));

  const duplicateSection = (id) => {
    setSections((prev) => {
      const idx = prev.findIndex((s) => s.id === id);
      const copy = { ...prev[idx], id: uid(), data: JSON.parse(JSON.stringify(prev[idx].data)) };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
  };

  const moveSection = (id, dir) => {
    setSections((prev) => {
      const idx = prev.findIndex((s) => s.id === id);
      if (idx + dir < 0 || idx + dir >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[idx + dir]] = [next[idx + dir], next[idx]];
      return next;
    });
  };

  const updateData = (id, newData) =>
    setSections((p) => p.map((s) => (s.id === id ? { ...s, data: newData } : s)));

  return (
    <div style={{ maxWidth: 820, margin: "0 auto", padding: "32px 20px 80px" }}>

      {sections.length === 0 && (
        <div style={{ textAlign: "center", padding: "70px 30px", background: "#fff", borderRadius: 18, border: "2px dashed #e5e7eb", marginBottom: 16 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📄</div>
          <div style={{ fontWeight: 700, fontSize: 18, color: "#0f172a" }}>No sections yet</div>
          <div style={{ color: "#9ca3af", fontSize: 14, marginTop: 6 }}>Click "Add Section" below to start building</div>
        </div>
      )}

      {sections.map((sec, idx) => {
        const meta = SECTION_TYPES.find((t) => t.type === sec.type);
        const Editor = EDITORS[sec.type];
        const isOpen = expanded === sec.id;
        return (
          <div key={sec.id}>
            <InsertStrip onClick={() => setPicker(idx)} />
            <div
              style={{
                background: "#fff", borderRadius: 14,
                border: `1.5px solid ${isOpen ? meta.color : "#e5e7eb"}`,
                overflow: "hidden", marginBottom: 2,
                boxShadow: isOpen ? `0 0 0 3px ${meta.color}18` : "none",
                transition: "border-color .2s, box-shadow .2s",
              }}
            >
              <div
                onClick={() => setExpanded(isOpen ? null : sec.id)}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 17px", cursor: "pointer", userSelect: "none" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 9, background: meta.color + "18", display: "flex", alignItems: "center", justifyContent: "center", color: meta.color, fontWeight: 900, fontSize: 18, flexShrink: 0 }}>
                    {meta.icon}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>{meta.label}</div>
                    <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 1 }}>{meta.hint}</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
                  <SmallBtn onClick={() => moveSection(sec.id, -1)}>↑</SmallBtn>
                  <SmallBtn onClick={() => moveSection(sec.id, 1)}>↓</SmallBtn>
                  <SmallBtn accent onClick={() => duplicateSection(sec.id)}>Duplicate</SmallBtn>
                  <SmallBtn danger onClick={() => removeSection(sec.id)}>Remove</SmallBtn>
                  <span style={{ marginLeft: 4, color: "#9ca3af", fontSize: 12 }}>{isOpen ? "▲" : "▼"}</span>
                </div>
              </div>
              {isOpen && (
                <div style={{ borderTop: `1px solid ${meta.color}30`, padding: "20px 22px", background: "#fafbfc" }}>
                  <Editor data={sec.data} onChange={(d) => updateData(sec.id, d)} />
                </div>
              )}
            </div>
          </div>
        );
      })}

      {sections.length > 0 && <InsertStrip onClick={() => setPicker("end")} />}

      <div style={{ display: "flex", justifyContent: "center", marginTop: 22 }}>
        <button
          onClick={() => setPicker("end")}
          style={{ padding: "12px 36px", borderRadius: 12, border: "2px dashed #93c5fd", background: "#eff6ff", color: "#1a56db", fontWeight: 700, fontSize: 14.5, cursor: "pointer", fontFamily: "inherit" }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#dbeafe")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#eff6ff")}
        >
          + Add Section
        </button>
      </div>

      {picker !== null && (
        <SectionPicker
          onSelect={(typeDef) => { addSection(typeDef); setPicker(null); }}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
}

const DEFAULT_META = {
  order: 1,
  backgroundImage: "",
  imageUrl: "",
  Brandlogo: "",
  customer: "",
  headline: "",
  detailPageHeadline: "",
  url: "",
  industry: "",
  description: "",
  impact: "",
  techonolgyStack: [{ id: "1", name: "" }],
};

export default function AdminStory() {
  const [tab, setTab] = useState("meta");
  const [meta, setMeta] = useState(DEFAULT_META);
  const [sections, setSections] = useState([]);

  // const handleSave = () => {
  //   const storyDetail = generateHTML(sections);
  //   const output = {
  //     order: meta.order,
  //     imageUrl: meta.backgroundImage,
  //     backgroundImage: meta.backgroundImage,
  //     Brandlogo: meta.Brandlogo,
  //     customer: meta.customer,
  //     headline: meta.headline,
  //     url: meta.url,
  //     detailPageHeadline: meta.detailPageHeadline,
  //     industry: meta.industry,
  //     description: meta.description,
  //     impact: meta.impact,
  //     techonolgyStack: meta.techonolgyStack.map((t, i) => ({ id: String(i + 1), name: t.name })),
  //     storyDetail,
  //   };
  // };
  
  const handleSave = async () => {
  const storyDetail = generateHTML(sections);

  const payload = {
    order: meta.order,
    imageUrl: meta.backgroundImage,
    backgroundImage: meta.backgroundImage,
    brandLogo: meta.Brandlogo,
    customer: meta.customer,
    headline: meta.headline,
    url: meta.url,
    detailPageHeadline: meta.detailPageHeadline || meta.headline,
    industry: meta.industry,
    description: meta.description,
    impact: meta.impact,
    techStack: meta.techonolgyStack.map((t, i) => ({
      id: String(i + 1),
      name: t.name
    })),
    storyDetail,
    createdBy: "Admin",
    updatedBy: "Admin"
  };

  try {
    const localApiBaseUrl =
      process.env.NODE_ENV === "development" &&
      typeof window !== "undefined" &&
      /^(localhost|127\.0\.0\.1|0\.0\.0\.0|::1)$/.test(window.location.hostname) &&
      window.location.port &&
      window.location.port !== "3000"
        ? ["http://", "localhost", ":3000"].join("")
        : "";
    const apiBaseUrl = (process.env.REACT_APP_PRODUCTION_API_URL || localApiBaseUrl).replace(/\/$/, "");
    const response = await fetch(
      `${apiBaseUrl}/backend/api/v1.0/AntrazalSolutionsPrivatelimited/clientstory`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      }
    );

    const result = await response.json();



  } catch (error) {
    console.error("❌ API Error:", error);
  }
};

  const metaFilled = meta.customer && meta.headline && meta.url && meta.industry && meta.description;
  const sectionCount = sections.length;

  const TABS = [
    { id: "meta",     label: "① Metadata",       badge: metaFilled ? "✓" : null,              badgeColor: "#16a34a" },
    { id: "sections", label: "② Story Sections",  badge: sectionCount > 0 ? String(sectionCount) : null, badgeColor: "#1a56db" },
    { id: "output",   label: "③ JSON Output",     badge: null,                                 badgeColor: "" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#f1f5f9", padding:"9rem 0"}}>
      <div style={{ position: "sticky", top: 0, zIndex: 50, background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "0 24px", height: 62, display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 1px 4px rgba(0,0,0,.06)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontWeight: 800, fontSize: 17, color: "#0f172a", letterSpacing: "-0.02em", marginRight: 10 }}>
            Story Builder
          </span>
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: "none", background: tab === t.id ? "#1a56db" : "transparent", color: tab === t.id ? "#fff" : "#6b7280", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit", transition: "all .15s" }}
            >
              {t.label}
              {t.badge && (
                <span style={{ background: tab === t.id ? "rgba(255,255,255,.25)" : t.badgeColor, color: "#fff", borderRadius: 20, padding: "0px 7px", fontSize: 11, fontWeight: 800 }}>
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        <button
          onClick={handleSave}
          style={{ padding: "9px 22px", borderRadius: 9, border: "none", background: "linear-gradient(135deg,#1a56db,#1e40af)", color: "#fff", fontWeight: 700, fontSize: 13.5, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 2px 8px rgba(26,86,219,.35)" }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = ".85")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
        >
          Save to Console
        </button>
      </div>
      {tab === "meta"     && <MetadataForm meta={meta} onChange={setMeta} />}
      {tab === "sections" && <StorySectionsBuilder sections={sections} setSections={setSections} />}
      {tab === "output"   && <OutputPanel meta={meta} sections={sections} />}
    </div>
  );
}
